import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import { CartEventosServicosService } from '@/features/servicos/pessoa-juridica/content/cart-eventos-servicos/cart-eventos-servicos.service';
import type { CartEventoResponse } from '@/core/api/sicom-api/servicos/pessoa-juridica/cart-evento/cart-evento.model';
import { formatarDataHoraMascara, montarPatchFormulario, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-cart-eventos-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, FormsModule, CardContent, DialogModule, VisualizadorPdf, InputText, InputMask],
    templateUrl: './cart-eventos-servicos.html',
    styleUrl: './cart-eventos-servicos.scss'
})
export class CartEventosServicos implements OnInit {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;

    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');

    readonly dados = signal<CartEventoResponse | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-cart-evento.pdf');

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        eventoNome: [''],
        eventoPublicoPrevisto: [''],
        eventoLocal: [''],
        eventoNatureza: [''],
        fareDataHoraInicio: [''],
        fareDataHoraFim: [''],
        fareNumero: [''],
        hospitalReferencia: [''],
        hospitalCnpj: ['']
    });
    private readonly servico = inject(CartEventosServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Recarrega ao mudar protocolo/UF (o form já existe: efeitos correm após ngOnInit).
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    ngOnInit(): void {
        // A solicitacao e somente leitura: a efetivacao registra o evento, mas os
        // campos nao sao editados/persistidos por esta tela.
        this.form.disable({ emitEvent: false });
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.form.reset({}, { emitEvent: false });
            this.form.disable({ emitEvent: false });
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    const patch = montarPatchFormulario(
                        this.form,
                        response as Record<string, unknown>,
                        {
                            eventoNome: 'nome',
                            eventoPublicoPrevisto: 'publico',
                            eventoLocal: 'local',
                            eventoNatureza: ['dsNatureza', 'idNatureza'],
                            fareDataHoraInicio: 'dataInicio',
                            fareDataHoraFim: 'dataFim',
                            fareNumero: 'fare',
                            hospitalReferencia: 'razaoSocialHospitaReferencia',
                            hospitalCnpj: 'cnpjHospitalReferencia'
                        },
                        {
                            fareDataHoraInicio: formatarDataHoraMascara,
                            fareDataHoraFim: formatarDataHoraMascara
                        }
                    );

                    this.form.patchValue(patch, { emitEvent: false });
                    this.form.disable({ emitEvent: false });
                },
                error: () => {
                    this.dados.set(null);
                    this.form.reset({}, { emitEvent: false });
                    this.form.disable({ emitEvent: false });
                }
            });
    }

    possuiPagamento(): boolean {
        return !!(this.dados()?.nossoNumero || this.dados()?.valor != null || this.dados()?.banco);
    }

    possuiAnexo(): boolean {
        return !!valorTexto(this.dados()?.linkAnexo).trim();
    }

    formatarValor(valor?: number | null): string {
        if (valor == null) {
            return '-';
        }
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
    }

    valorOuTraco(valor: unknown): string {
        const texto = valorTexto(valor).trim();
        return texto ? texto : '-';
    }

    onVisualizarAnexo(event: Event): void {
        event.preventDefault();

        const idAnexo = this.extrairIdAnexo(this.dados()?.linkAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback textual.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }
}
